import React, {
  useState, useEffect, useCallback, useMemo, useRef, useContext,
} from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import debounce from 'lodash/debounce';
import dayjs from 'dayjs';
import Fuse from 'fuse.js';
import { AuthContext } from 'util/Auth';
import {
  Button, Space, Tag, Table, InputNumber, message, Modal,
  Typography, Spin, Popconfirm, Progress, Tooltip, Input, Switch, Select, Alert,
  Collapse, Checkbox, Dropdown, Divider, Radio,
} from 'antd';
import {
  ArrowLeftOutlined, SaveOutlined, CheckCircleOutlined, CloudUploadOutlined,
  DownloadOutlined, DeleteOutlined, PlusOutlined, PercentageOutlined,
  DatabaseOutlined, RollbackOutlined, ThunderboltOutlined, ColumnHeightOutlined,
  SettingOutlined, CalculatorOutlined,
} from '@ant-design/icons';
import * as api from 'util/api';
import {
  PRICE_FIELDS,
  skuContainerType,
  isKegSku,
  priceFieldGreyOut,
  priceFieldGreyOutTooltip,
  priceFieldKeysHiddenForSkuFocus,
  calculateProgressivePrice,
} from 'util/pricing-fields';
import KountaProductLinker from 'components/KountaProductLinker/KountaProductLinker';
import SkuCin7InlineEditor from 'components/SkuCin7InlineEditor/SkuCin7InlineEditor';
import SkuPicker from './SkuPicker';
import {
  computeMargin,
  getEffectivePrices,
  buildTargetMap,
  resolveTargetPct,
  marginMeetsTarget,
  SELLING_PRICE_FIELDS_WITH_MARGIN,
} from './marginUtils';
import {
  KEG_POUR_FIELDS,
  getKegSuggestionsForSku,
  valueMatchesSuggestion,
  isHighAbvOrWild,
} from './kegPricingSuggestions';
import {
  calculateRrpSuggestions,
  calculateOpSuggestion,
} from './pricingSuggestions';
import './ProposalEditor.less';

const { Text, Title, Paragraph } = Typography;

// PRICE_FIELDS imported from util/pricing-fields

const AU_PRICING_KEYS = new Set(['priceAu', 'priceVicSa', 'priceQldWa']);

/** snake_case → column label for preview tables */
const SNAKE_FIELD_LABEL = {
  rrp_unit: 'RRP Unit',
  rrp_pack: 'RRP Pack',
  rrp_case: 'RRP Case',
  price_on: 'On Prem',
  price_off: 'Off Prem',
  price_progressive: 'Progressive',
  price_au: 'AU',
  price_vic_sa: 'VIC/SA',
  price_qld_wa: 'QLD/WA',
  price_venue_per_litre: 'Venue/L',
  price_pint: 'Pint',
  price_half_pint: 'Half Pint',
  price_150ml: '150ml',
  price_500ml: '500ml',
  price_bar: 'O.P.',
};

function buildProposedOverridesPayload(editedPrices) {
  const out = {};
  Object.entries(editedPrices || {}).forEach(([itemId, prices]) => {
    if (prices && Object.keys(prices).length > 0) {
      out[itemId] = prices;
    }
  });
  return out;
}

function formatCin7Tiers(tiers) {
  if (!tiers || typeof tiers !== 'object') return '—';
  const entries = Object.entries(tiers).filter(([, v]) => v != null && v !== '');
  if (!entries.length) return '—';
  return entries.map(([k, v]) => `${k}=${v}`).join('; ');
}

// SKU type helpers and grey-out logic imported from util/pricing-fields

/** Top-level order when grouping the table by SKU container type. */
const SKU_TYPE_GROUP_ORDER = ['keg', 'can', 'bottle', 'other'];

const SKU_TYPE_GROUP_LABELS = {
  keg: 'Kegs',
  can: 'Cans',
  bottle: 'Bottles',
  other: 'Other',
};

/** Extract normalised volume string from a SKU for grouping. */
function skuVolumeKey(sku) {
  const raw = sku?.unitType ?? sku?.unit_type;
  if (raw == null || String(raw).trim() === '') return '__unspecified__';
  return String(raw).trim();
}

/**
 * Sort volume keys so numeric-prefixed values (e.g. "19.5L", "50L") sort
 * numerically, with non-numeric / unspecified at the end.
 */
function compareVolumeKeys(a, b) {
  if (a === '__unspecified__' && b === '__unspecified__') return 0;
  if (a === '__unspecified__') return 1;
  if (b === '__unspecified__') return -1;
  const na = parseFloat(a);
  const nb = parseFloat(b);
  if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
  if (!Number.isNaN(na)) return -1;
  if (!Number.isNaN(nb)) return 1;
  return a.localeCompare(b);
}

/** Bucket for proposal row grouping: keg | can | bottle | other (unknown / empty). */
function skuTypeCategoryKey(sku) {
  const ct = skuContainerType(sku);
  if (ct === 'keg') return 'keg';
  if (ct === 'can') return 'can';
  if (ct === 'bottle') return 'bottle';
  return 'other';
}

/** True if edited prices differ from server proposal items (camelCase). */
function proposalHasUnsavedChanges(editedPrices, proposal) {
  if (!proposal?.items?.length) return false;
  return proposal.items.some((item) => {
    const a = editedPrices[item.id] || {};
    const b = item.proposedPrices || {};
    const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
    return [...keys].some((k) => String(a[k] ?? '') !== String(b[k] ?? ''));
  });
}

function getCin7SkuString(record) {
  const sku = record.productSku;
  return String(sku?.cin7Sku ?? sku?.cin7_sku ?? '');
}

function priceSortValue(record, pfKey, editedPrices) {
  const current = record.currentPrices?.[pfKey];
  const proposed = editedPrices[record.id]?.[pfKey];
  const v = proposed != null && proposed !== '' ? proposed : current;
  if (v == null || v === '') return null;
  const n = parseFloat(String(v));
  return Number.isNaN(n) ? null : n;
}

function hasPriceChange(record, pfKey, editedPrices) {
  const current = record.currentPrices?.[pfKey];
  const proposed = editedPrices[record.id]?.[pfKey];
  return proposed != null && proposed !== '' && proposed !== current;
}

function priceFilterMatch(record, pfKey, filterValue, editedPrices) {
  if (filterValue == null || filterValue === '') return true;
  const v = Array.isArray(filterValue) ? filterValue[0] : filterValue;
  const changed = hasPriceChange(record, pfKey, editedPrices);
  if (v === 'changed') return changed;
  if (v === 'unchanged') return !changed;
  return true;
}

function productNameSortKey(record) {
  return (record.productName || '').toLowerCase();
}

function skuNameSortKey(record) {
  const sku = record.productSku;
  return (sku?.name || `SKU #${record.productSkuKey}` || '').toLowerCase();
}

/**
 * Apply price edit + keg pour cascade + off-prem → progressive cascade + auto-suggested flags.
 * @returns {{ nextEdited: object, nextAuto: object }}
 */
function skuWithRange(item) {
  const sku = item?.productSku;
  if (!sku) return sku;
  return {
    ...sku,
    productRange: item.productRange ?? item.product_range,
    productType: item.productType ?? item.product_type,
  };
}

function applyIfAuto(nextFields, itemAuto, prevAuto, itemId, key, suggested) {
  if (suggested == null) return;
  const prevVal = nextFields[key];
  const wasAuto = prevAuto[itemId]?.[key];
  const shouldApply = wasAuto || prevVal == null || prevVal === '';
  if (!shouldApply) return;
  // Local copies owned by applyPriceCascadeAfterEdit.
  // eslint-disable-next-line no-param-reassign
  nextFields[key] = String(suggested);
  // eslint-disable-next-line no-param-reassign
  itemAuto[key] = true;
}

function applyPriceCascadeAfterEdit(
  prevEdited, prevAuto, itemId, field, value, item, targetMap, rules,
) {
  const nextFields = { ...(prevEdited[itemId] || {}) };
  if (value == null) {
    delete nextFields[field];
  } else {
    nextFields[field] = String(value);
  }

  const itemAuto = { ...(prevAuto[itemId] || {}) };
  if (KEG_POUR_FIELDS.includes(field)) {
    delete itemAuto[field];
  }
  if (field === 'priceProgressive') {
    delete itemAuto.priceProgressive;
  }

  const sku = skuWithRange(item);
  const abv = item.productAbv ?? item.product_abv;
  const productType = item.productType ?? item.product_type;
  const kegOpts = { productType, rules };

  if (field === 'priceOn' && isKegSku(sku)) {
    const effOn = nextFields.priceOn ?? item.currentPrices?.priceOn;
    if (effOn == null || effOn === '') {
      KEG_POUR_FIELDS.forEach((k) => {
        delete itemAuto[k];
      });
    } else {
      const sug = getKegSuggestionsForSku(sku, effOn, abv, targetMap, kegOpts);
      if (sug) {
        KEG_POUR_FIELDS.forEach((k) => {
          applyIfAuto(nextFields, itemAuto, prevAuto, itemId, k, sug[k]);
        });
      }
    }
  }

  if (field === 'priceOff' && !isKegSku(sku)) {
    const effOff = nextFields.priceOff ?? item.currentPrices?.priceOff;
    if (effOff == null || effOff === '') {
      delete itemAuto.priceProgressive;
      delete itemAuto.rrpUnit;
      delete itemAuto.rrpPack;
      delete itemAuto.rrpCase;
      delete itemAuto.priceBar;
    } else {
      const sug = calculateProgressivePrice(effOff, sku);
      applyIfAuto(nextFields, itemAuto, prevAuto, itemId, 'priceProgressive', sug);
      const mergedPrices = {
        ...(item.currentPrices || {}),
        ...nextFields,
      };
      const rrp = calculateRrpSuggestions(sku, mergedPrices, targetMap, rules);
      applyIfAuto(nextFields, itemAuto, prevAuto, itemId, 'rrpUnit', rrp.rrpUnit);
      applyIfAuto(nextFields, itemAuto, prevAuto, itemId, 'rrpPack', rrp.rrpPack);
      applyIfAuto(nextFields, itemAuto, prevAuto, itemId, 'rrpCase', rrp.rrpCase);
      const op = calculateOpSuggestion(sku, mergedPrices, rules);
      applyIfAuto(nextFields, itemAuto, prevAuto, itemId, 'priceBar', op);
    }
  }

  return {
    nextEdited: { ...prevEdited, [itemId]: nextFields },
    nextAuto: { ...prevAuto, [itemId]: itemAuto },
  };
}

const ProposalEditor = ({
  proposalId, onBack,
}) => {
  const history = useHistory();
  const { currentUser } = useContext(AuthContext);
  const [proposal, setProposal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [applying, setApplying] = useState(false);
  const [pushingCin7, setPushingCin7] = useState(false);
  const [cin7Status, setCin7Status] = useState(null);
  const [editedPrices, setEditedPrices] = useState({});
  /** Per proposal item, which fields were filled by cascade / bulk autofill (for green highlight). */
  const [autoSuggestedFields, setAutoSuggestedFields] = useState({});
  const [skuPickerVisible, setSkuPickerVisible] = useState(false);
  const [selectedSkuKeys, setSelectedSkuKeys] = useState([]);
  const [skuSearchTerm, setSkuSearchTerm] = useState('');
  /** Hidden column keys (default: AU pricing columns hidden, matching previous UX). */
  const [hiddenColumns, setHiddenColumns] = useState(
    () => new Set(['priceAu', 'priceVicSa', 'priceQldWa']),
  );
  const [searchTerm, setSearchTerm] = useState('');
  /** flat = no grouping; product = by Garage product name; skuType = by container; volume = by unit size (e.g. 19.5L / 50L). */
  const [tableGroupMode, setTableGroupMode] = useState('flat');
  /** null = all SKU types; keg | can | bottle = filter rows and hide irrelevant price columns. */
  const [skuFocusMode, setSkuFocusMode] = useState(null);
  const [tableFilters, setTableFilters] = useState({});
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [marginTargets, setMarginTargets] = useState([]);
  const [pricingRules, setPricingRules] = useState({});

  const [applyConfirmOpen, setApplyConfirmOpen] = useState(false);
  const [applyPreviewLoading, setApplyPreviewLoading] = useState(false);
  const [applyPreviewChanges, setApplyPreviewChanges] = useState([]);

  const [pushConfirmOpen, setPushConfirmOpen] = useState(false);
  const [pushPreviewLoading, setPushPreviewLoading] = useState(false);
  const [pushPreviewItems, setPushPreviewItems] = useState([]);

  const [recoverDbOpen, setRecoverDbOpen] = useState(false);
  const [recoverDbLoading, setRecoverDbLoading] = useState(false);
  const [recoverDbChanges, setRecoverDbChanges] = useState([]);
  const [recoveringDb, setRecoveringDb] = useState(false);
  const [recoverCin7Open, setRecoverCin7Open] = useState(false);
  const [recoverCin7Loading, setRecoverCin7Loading] = useState(false);
  const [recoverCin7Items, setRecoverCin7Items] = useState([]);
  const [recoverDbSelectedSkuKeys, setRecoverDbSelectedSkuKeys] = useState([]);
  const [recoverCin7SelectedKeys, setRecoverCin7SelectedKeys] = useState([]);

  const [fullSendOpen, setFullSendOpen] = useState(false);
  const [fullSendLoading, setFullSendLoading] = useState(false);
  const [fullSendApplyPreview, setFullSendApplyPreview] = useState([]);
  const [fullSendCin7Preview, setFullSendCin7Preview] = useState([]);
  const [fullSending, setFullSending] = useState(false);

  /** Single shared loads for Pricing Manager table rows (B2 — avoid N× full catalog fetches). */
  const [sharedKountaCatalog, setSharedKountaCatalog] = useState(null);
  const [sharedCin7CachedProducts, setSharedCin7CachedProducts] = useState(null);

  const editedPricesRef = useRef(editedPrices);
  const proposalRef = useRef(proposal);
  const autoSuggestedRef = useRef(autoSuggestedFields);
  const marginTargetMapRef = useRef({});
  const pricingRulesRef = useRef({});
  useEffect(() => { editedPricesRef.current = editedPrices; }, [editedPrices]);
  useEffect(() => { proposalRef.current = proposal; }, [proposal]);
  useEffect(() => { autoSuggestedRef.current = autoSuggestedFields; }, [autoSuggestedFields]);

  useEffect(() => {
    if (!currentUser?.uid) return;
    setAutoSaveEnabled(localStorage.getItem(`${currentUser.uid}.enableAutoSave`) === 'true');
  }, [currentUser?.uid]);

  const loadProposal = useCallback(async (opts = {}) => {
    const silent = opts.silent === true;
    const refreshPrices = opts.refreshPrices === true;
    const shouldSyncEditedPrices = !silent || refreshPrices;
    if (!silent) setLoading(true);
    try {
      const data = await api.getPricingProposal(proposalId);
      setProposal(data);
      if (shouldSyncEditedPrices) {
        const prices = {};
        (data.items || []).forEach((item) => {
          prices[item.id] = item.proposedPrices || {};
        });
        setEditedPrices(prices);
        setAutoSuggestedFields({});
      }
    } catch (err) {
      message.error('Failed to load proposal');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [proposalId]);

  const refreshProposalAfterSkuLink = useCallback(async () => {
    await loadProposal({ silent: true });
    try {
      const fresh = await api.getKountaProducts();
      setSharedKountaCatalog(Array.isArray(fresh) ? fresh : []);
    } catch (e) {
      console.error('Failed to refresh Kounta catalog after link change', e);
    }
  }, [loadProposal]);

  useEffect(() => { loadProposal(); }, [loadProposal]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [kountaAll, cin7] = await Promise.all([
          api.getKountaProducts(),
          api.getCachedCin7Products(),
        ]);
        if (cancelled) return;
        setSharedKountaCatalog(Array.isArray(kountaAll) ? kountaAll : []);
        setSharedCin7CachedProducts(Array.isArray(cin7) ? cin7 : []);
      } catch (e) {
        if (!cancelled) {
          message.error('Failed to load Kounta/Cin7 reference data');
          setSharedKountaCatalog([]);
          setSharedCin7CachedProducts([]);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [proposalId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [rows, rules] = await Promise.all([
          api.listMarginTargets(),
          api.listPricingRules(),
        ]);
        if (!cancelled) {
          setMarginTargets(rows);
          setPricingRules(rules || {});
        }
      } catch {
        if (!cancelled) {
          setMarginTargets([]);
          setPricingRules({});
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const marginTargetMap = useMemo(() => buildTargetMap(marginTargets), [marginTargets]);
  useEffect(() => {
    marginTargetMapRef.current = marginTargetMap;
  }, [marginTargetMap]);
  useEffect(() => {
    pricingRulesRef.current = pricingRules;
  }, [pricingRules]);

  const saveDraftPricesToServer = useCallback(async () => {
    const ep = editedPricesRef.current;
    const updates = Object.entries(ep).map(([itemId, prices]) => (
      api.updateProposalItem(proposalId, itemId, prices)
    ));
    await Promise.all(updates);
    const data = await api.getPricingProposal(proposalId);
    setProposal(data);
    const prices = {};
    (data.items || []).forEach((item) => {
      prices[item.id] = item.proposedPrices || {};
    });
    setEditedPrices(prices);
    setAutoSuggestedFields({});
  }, [proposalId]);

  const runAutoSaveRef = useRef(() => {});

  const debouncedAutoSave = useMemo(
    () => debounce(() => {
      runAutoSaveRef.current();
    }, 1500),
    [],
  );

  useEffect(() => {
    runAutoSaveRef.current = async () => {
      if (!autoSaveEnabled || proposalRef.current?.status !== 'draft') return;
      if (!currentUser?.admin) return;
      const ep = editedPricesRef.current;
      const prop = proposalRef.current;
      if (!proposalHasUnsavedChanges(ep, prop)) return;
      setSaving(true);
      try {
        const updates = Object.entries(ep).map(([itemId, prices]) => (
          api.updateProposalItem(proposalId, itemId, prices)
        ));
        await Promise.all(updates);
        const data = await api.getPricingProposal(proposalId);
        setProposal(data);
        const prices = {};
        (data.items || []).forEach((item) => {
          prices[item.id] = item.proposedPrices || {};
        });
        setEditedPrices(prices);
        setAutoSuggestedFields({});
        setLastSaved(dayjs());
      } catch {
        message.error('Auto-save failed');
      } finally {
        setSaving(false);
      }
    };
  });

  useEffect(() => {
    if (!autoSaveEnabled || proposal?.status !== 'draft' || !currentUser?.admin) {
      debouncedAutoSave.cancel();
      return undefined;
    }
    debouncedAutoSave();
    return () => debouncedAutoSave.cancel();
  }, [editedPrices, autoSaveEnabled, proposal?.status, currentUser?.admin, debouncedAutoSave]);

  const onToggleAutoSave = (enabled) => {
    if (currentUser?.uid) {
      localStorage.setItem(`${currentUser.uid}.enableAutoSave`, enabled);
    }
    setAutoSaveEnabled(enabled);
    if (enabled) {
      debouncedAutoSave.flush();
    } else {
      debouncedAutoSave.cancel();
    }
  };

  const isDraft = proposal?.status === 'draft';

  const handlePriceChange = useCallback((itemId, field, value) => {
    const item = proposalRef.current?.items?.find((i) => i.id === itemId);
    if (!item) {
      setEditedPrices((prev) => {
        const nextFields = { ...(prev[itemId] || {}) };
        if (value == null) {
          delete nextFields[field];
        } else {
          nextFields[field] = String(value);
        }
        return { ...prev, [itemId]: nextFields };
      });
      return;
    }

    setEditedPrices((prevEdited) => {
      const prevAuto = autoSuggestedRef.current;
      const { nextEdited, nextAuto } = applyPriceCascadeAfterEdit(
        prevEdited,
        prevAuto,
        itemId,
        field,
        value,
        item,
        marginTargetMapRef.current,
        pricingRulesRef.current,
      );
      autoSuggestedRef.current = nextAuto;
      setAutoSuggestedFields(nextAuto);
      return nextEdited;
    });
  }, []);

  const handleBulkAutofillPourPrices = useCallback(() => {
    const items = proposalRef.current?.items || [];
    const prevEdited = editedPricesRef.current;
    const prevAuto = autoSuggestedRef.current;
    const nextEdited = { ...prevEdited };
    const nextAuto = { ...prevAuto };
    let filled = 0;
    let skipped = 0;

    items.forEach((item) => {
      if (!isKegSku(item.productSku)) return;
      const effOn = nextEdited[item.id]?.priceOn ?? item.currentPrices?.priceOn;
      if (effOn == null || effOn === '') {
        skipped += 1;
        return;
      }
      const sug = getKegSuggestionsForSku(
        skuWithRange(item),
        effOn,
        item.productAbv ?? item.product_abv,
        marginTargetMapRef.current,
        {
          productType: item.productType ?? item.product_type,
          rules: pricingRulesRef.current,
        },
      );
      if (!sug) {
        skipped += 1;
        return;
      }
      const row = { ...(nextEdited[item.id] || {}) };
      const itemAuto = { ...(nextAuto[item.id] || {}) };
      KEG_POUR_FIELDS.forEach((k) => {
        const s = sug[k];
        if (s == null) return;
        row[k] = String(s);
        itemAuto[k] = true;
      });
      nextEdited[item.id] = row;
      nextAuto[item.id] = itemAuto;
      filled += 1;
    });

    autoSuggestedRef.current = nextAuto;
    setEditedPrices(nextEdited);
    setAutoSuggestedFields(nextAuto);
    message.success(
      `Auto-filled pour prices for ${filled} keg SKU(s)${skipped ? ` (${skipped} skipped — no keg price)` : ''}`,
    );
  }, []);

  const handleBulkAutofillProgressivePrices = useCallback(() => {
    const items = proposalRef.current?.items || [];
    const prevEdited = editedPricesRef.current;
    const prevAuto = autoSuggestedRef.current;
    const nextEdited = { ...prevEdited };
    const nextAuto = { ...prevAuto };
    let filled = 0;
    let skipped = 0;

    items.forEach((item) => {
      if (isKegSku(item.productSku)) return;
      const effOff = nextEdited[item.id]?.priceOff ?? item.currentPrices?.priceOff;
      if (effOff == null || effOff === '') {
        skipped += 1;
        return;
      }
      const sug = calculateProgressivePrice(effOff, item.productSku);
      if (sug == null) {
        skipped += 1;
        return;
      }
      const row = { ...(nextEdited[item.id] || {}) };
      const itemAuto = { ...(nextAuto[item.id] || {}) };
      row.priceProgressive = String(sug);
      itemAuto.priceProgressive = true;
      nextEdited[item.id] = row;
      nextAuto[item.id] = itemAuto;
      filled += 1;
    });

    autoSuggestedRef.current = nextAuto;
    setEditedPrices(nextEdited);
    setAutoSuggestedFields(nextAuto);
    message.success(
      `Auto-filled Progressive for ${filled} SKU(s)${skipped ? ` (${skipped} skipped — no Off Prem or keg)` : ''}`,
    );
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveDraftPricesToServer();
      message.success('Prices saved');
      setLastSaved(dayjs());
    } catch (err) {
      message.error('Failed to save prices');
    } finally {
      setSaving(false);
    }
  };

  const handleApply = async () => {
    setApplying(true);
    try {
      await handleSave();
      const result = await api.applyPricingProposal(proposalId);
      message.success(`Applied: ${result.updated} SKUs updated`);
      if (result.errors?.length) {
        message.warning(`${result.errors.length} errors occurred`);
      }
      await loadProposal();
    } catch (err) {
      message.error('Failed to apply proposal');
    } finally {
      setApplying(false);
    }
  };

  const pollCin7Status = async () => {
    const poll = async () => {
      try {
        const status = await api.getCin7PushStatus();
        setCin7Status(status);
        if (status.status === 'running') {
          setTimeout(poll, 2000);
        } else {
          setPushingCin7(false);
          if (status.status === 'completed') {
            message.success(`Cin7 push complete: ${status.processed} products updated`);
          } else if (status.status === 'failed') {
            message.error('Cin7 push failed');
          }
        }
      } catch {
        setPushingCin7(false);
      }
    };
    poll();
  };

  const handlePushCin7 = async () => {
    setPushingCin7(true);
    try {
      debouncedAutoSave.cancel();
      if (proposal?.status === 'draft' && proposalHasUnsavedChanges(editedPrices, proposal)) {
        setSaving(true);
        try {
          await saveDraftPricesToServer();
          setLastSaved(dayjs());
        } finally {
          setSaving(false);
        }
      }
      const { data, status } = await api.pushCin7Prices(proposalId);
      if (status === 409) {
        message.warning('Cin7 push already in progress');
        setPushingCin7(false);
        return;
      }
      message.info(`Cin7 push started for ${data.total} SKUs`);
      pollCin7Status();
    } catch (err) {
      message.error('Failed to start Cin7 push');
      setPushingCin7(false);
    }
  };

  const handleExportKounta = async () => {
    try {
      await api.exportKountaCsv(proposalId);
      message.success('Kounta CSV downloaded');
    } catch (err) {
      message.error('Failed to export Kounta CSV');
    }
  };

  const handleExportKountaPrevious = async () => {
    try {
      await api.exportKountaCsv(proposalId, { previous: true });
      message.success('Previous Kounta CSV downloaded (import in Kounta to restore)');
    } catch (err) {
      message.error('Failed to export recovery CSV');
    }
  };

  const openApplyPreviewModal = async () => {
    setApplyConfirmOpen(true);
    setApplyPreviewLoading(true);
    setApplyPreviewChanges([]);
    try {
      debouncedAutoSave.cancel();
      if (proposal?.status === 'draft' && proposalHasUnsavedChanges(editedPrices, proposal)) {
        setSaving(true);
        try {
          await saveDraftPricesToServer();
          setLastSaved(dayjs());
        } finally {
          setSaving(false);
        }
      }
      const proposedOverrides = buildProposedOverridesPayload(editedPricesRef.current);
      const { changes } = await api.previewPricingApply(proposalId, proposedOverrides);
      setApplyPreviewChanges(Array.isArray(changes) ? changes : []);
    } catch (e) {
      message.error('Failed to load apply preview');
      setApplyConfirmOpen(false);
    } finally {
      setApplyPreviewLoading(false);
    }
  };

  const openPushPreviewModal = async () => {
    setPushConfirmOpen(true);
    setPushPreviewLoading(true);
    setPushPreviewItems([]);
    try {
      debouncedAutoSave.cancel();
      if (proposal?.status === 'draft' && proposalHasUnsavedChanges(editedPrices, proposal)) {
        setSaving(true);
        try {
          await saveDraftPricesToServer();
          setLastSaved(dayjs());
        } finally {
          setSaving(false);
        }
      }
      const proposedOverrides = buildProposedOverridesPayload(editedPricesRef.current);
      const { items } = await api.previewPricingCin7(proposalId, proposedOverrides);
      setPushPreviewItems(Array.isArray(items) ? items : []);
    } catch (e) {
      message.error('Failed to load Cin7 preview');
      setPushConfirmOpen(false);
    } finally {
      setPushPreviewLoading(false);
    }
  };

  const openFullSendModal = async () => {
    if (!isDraft) return;
    setFullSendOpen(true);
    setFullSendLoading(true);
    setFullSendApplyPreview([]);
    setFullSendCin7Preview([]);
    try {
      debouncedAutoSave.cancel();
      if (proposal?.status === 'draft' && proposalHasUnsavedChanges(editedPrices, proposal)) {
        setSaving(true);
        try {
          await saveDraftPricesToServer();
          setLastSaved(dayjs());
        } finally {
          setSaving(false);
        }
      }
      const proposedOverrides = buildProposedOverridesPayload(editedPricesRef.current);
      const [applyRes, cin7Res] = await Promise.all([
        api.previewPricingApply(proposalId, proposedOverrides),
        api.previewPricingCin7(proposalId, proposedOverrides),
      ]);
      setFullSendApplyPreview(Array.isArray(applyRes?.changes) ? applyRes.changes : []);
      setFullSendCin7Preview(Array.isArray(cin7Res?.items) ? cin7Res.items : []);
    } catch (e) {
      message.error('Failed to load Full Send preview');
      setFullSendOpen(false);
    } finally {
      setFullSendLoading(false);
    }
  };

  const handleFullSend = async () => {
    setFullSendOpen(false);
    setFullSending(true);
    setApplying(true);
    try {
      debouncedAutoSave.cancel();
      if (proposal?.status === 'draft' && proposalHasUnsavedChanges(editedPrices, proposal)) {
        await saveDraftPricesToServer();
        setLastSaved(dayjs());
      }
      const applyResult = await api.applyPricingProposal(proposalId);
      message.success(`Matrix DB: ${applyResult.updated} SKU(s) updated; Monday.com synced where applicable`);
      if (applyResult.errors?.length) {
        message.warning(`${applyResult.errors.length} error(s) during apply`);
      }

      setPushingCin7(true);
      try {
        const { data, status } = await api.pushCin7Prices(proposalId);
        if (status === 409) {
          message.warning('Cin7 push already in progress — skipped starting a new job');
          setPushingCin7(false);
        } else {
          message.info(`Cin7 push started for ${data.total} SKU(s)`);
          pollCin7Status();
        }
      } catch (err) {
        message.error('Failed to start Cin7 push');
        setPushingCin7(false);
      }

      try {
        await api.exportKountaCsv(proposalId);
        message.success('Kounta CSV downloaded — import manually in Kounta');
      } catch (err) {
        message.error('Failed to export Kounta CSV');
      }

      await loadProposal({ refreshPrices: true });
    } catch (err) {
      message.error('Full Send failed at apply step');
    } finally {
      setFullSending(false);
      setApplying(false);
    }
  };

  const openRecoverDbModal = async () => {
    setRecoverDbOpen(true);
    setRecoverDbLoading(true);
    setRecoverDbChanges([]);
    setRecoverDbSelectedSkuKeys([]);
    try {
      const { changes } = await api.previewRecoverDb(proposalId);
      const arr = Array.isArray(changes) ? changes : [];
      setRecoverDbChanges(arr);
      const keys = [...new Set(
        arr.filter((c) => c.field && !c.error).map((c) => String(c.skuKey)),
      )];
      setRecoverDbSelectedSkuKeys(keys);
    } catch (e) {
      message.error('Failed to load recovery preview');
      setRecoverDbOpen(false);
    } finally {
      setRecoverDbLoading(false);
    }
  };

  const openRecoverCin7Modal = async () => {
    setRecoverCin7Open(true);
    setRecoverCin7Loading(true);
    setRecoverCin7Items([]);
    setRecoverCin7SelectedKeys([]);
    try {
      const { items } = await api.previewRecoverCin7(proposalId);
      const list = Array.isArray(items) ? items : [];
      setRecoverCin7Items(list);
      const ok = list.filter(
        (i) => !i.error && i.tiers && Object.keys(i.tiers).length > 0,
      );
      setRecoverCin7SelectedKeys(ok.map((i) => i.skuKey));
    } catch (e) {
      message.error('Failed to load recovery preview');
      setRecoverCin7Open(false);
    } finally {
      setRecoverCin7Loading(false);
    }
  };

  const confirmRecoverDb = async () => {
    setRecoverDbOpen(false);
    setRecoveringDb(true);
    try {
      const payload = recoverDbSelectedSkuKeys.length
        ? { skuKeys: recoverDbSelectedSkuKeys }
        : undefined;
      const result = await api.recoverPricingDb(proposalId, payload || {});
      message.success(`Restored ${result.updated} SKU(s) from snapshot`);
      if (result.errors?.length) {
        message.warning(`${result.errors.length} error(s)`);
      }
      await loadProposal({ refreshPrices: true });
    } catch (e) {
      message.error('Failed to restore Matrix DB');
    } finally {
      setRecoveringDb(false);
    }
  };

  const confirmRecoverCin7 = async () => {
    setRecoverCin7Open(false);
    setPushingCin7(true);
    try {
      const payload = recoverCin7SelectedKeys.length
        ? { skuKeys: recoverCin7SelectedKeys }
        : undefined;
      const { data, status } = await api.recoverPricingCin7(proposalId, payload || {});
      if (status === 409) {
        message.warning('Cin7 operation already in progress');
        setPushingCin7(false);
        return;
      }
      message.info(`Cin7 recovery started for ${data.total} SKU(s)`);
      pollCin7Status();
    } catch (e) {
      message.error('Failed to start Cin7 recovery');
      setPushingCin7(false);
    }
  };

  const handleRemoveItem = async (itemId) => {
    try {
      await api.deleteProposalItem(proposalId, itemId);
      message.success('Item removed');
      await loadProposal();
    } catch (err) {
      message.error('Failed to remove item');
    }
  };

  const handleAddSkus = async () => {
    if (!selectedSkuKeys.length) return;
    try {
      const result = await api.addProposalItems(proposalId, selectedSkuKeys);
      message.success(`Added ${result.added} SKUs (${result.skipped} already in proposal)`);
      setSkuPickerVisible(false);
      setSelectedSkuKeys([]);
      setSkuSearchTerm('');
      await loadProposal();
    } catch (err) {
      message.error('Failed to add SKUs');
    }
  };

  const proposalItems = proposal?.items || [];

  const fuseSearcher = useMemo(() => {
    if (!proposalItems.length) return null;
    return new Fuse(proposalItems, {
      keys: ['productName', 'productSku.name'],
      threshold: 0.3,
      ignoreLocation: true,
    });
  }, [proposalItems]);

  const searchFilteredItems = useMemo(() => {
    if (!searchTerm.trim()) return proposalItems;
    if (!fuseSearcher) return proposalItems;
    return fuseSearcher.search(searchTerm).map((r) => r.item);
  }, [proposalItems, searchTerm, fuseSearcher]);

  const focusHiddenPriceKeys = useMemo(
    () => priceFieldKeysHiddenForSkuFocus(skuFocusMode),
    [skuFocusMode],
  );

  const effectiveHiddenColumns = useMemo(() => {
    const next = new Set(hiddenColumns);
    focusHiddenPriceKeys.forEach((k) => next.add(k));
    return next;
  }, [hiddenColumns, focusHiddenPriceKeys]);

  const focusFilteredItems = useMemo(() => {
    if (!skuFocusMode) return searchFilteredItems;
    return searchFilteredItems.filter(
      (item) => skuTypeCategoryKey(item.productSku) === skuFocusMode,
    );
  }, [searchFilteredItems, skuFocusMode]);

  const tableDataSource = useMemo(() => {
    if (tableGroupMode === 'flat') return focusFilteredItems;

    if (tableGroupMode === 'product') {
      const map = new Map();
      focusFilteredItems.forEach((item) => {
        const pn = item.productName || '';
        if (!map.has(pn)) map.set(pn, []);
        map.get(pn).push(item);
      });
      return [...map.entries()]
        .sort((a, b) => String(a[0]).localeCompare(String(b[0])))
        .map(([pn, children]) => ({
          groupRow: true,
          groupKey: `group-product-${pn}`,
          productName: pn,
          children,
        }));
    }

    if (tableGroupMode === 'volume') {
      const map = new Map();
      focusFilteredItems.forEach((item) => {
        const vol = skuVolumeKey(item.productSku);
        if (!map.has(vol)) map.set(vol, []);
        map.get(vol).push(item);
      });
      const sortedKeys = [...map.keys()].sort(compareVolumeKeys);
      return sortedKeys.map((vol) => ({
        groupRow: true,
        groupKey: `group-volume-${vol}`,
        groupLabel: vol === '__unspecified__' ? 'Unspecified volume' : vol,
        children: map.get(vol),
      }));
    }

    // tableGroupMode === 'skuType'
    const map = new Map();
    focusFilteredItems.forEach((item) => {
      const cat = skuTypeCategoryKey(item.productSku);
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat).push(item);
    });
    return SKU_TYPE_GROUP_ORDER.filter((k) => map.has(k)).map((k) => ({
      groupRow: true,
      groupKey: `group-skutype-${k}`,
      groupLabel: SKU_TYPE_GROUP_LABELS[k],
      skuTypeGroupKey: k,
      children: map.get(k),
    }));
  }, [focusFilteredItems, tableGroupMode]);

  const productFilterOptions = useMemo(() => {
    const names = [...new Set(proposalItems.map((i) => i.productName || '').filter(Boolean))];
    names.sort((a, b) => a.localeCompare(b));
    return names.map((n) => ({ text: n, value: n }));
  }, [proposalItems]);

  const priceFieldsVisible = useMemo(
    () => PRICE_FIELDS.filter((f) => !effectiveHiddenColumns.has(f.key)),
    [effectiveHiddenColumns],
  );

  const pricingColumns = useMemo(() => {
    const cols = [
      {
        title: (
          <Tooltip title="Garage Project product name and Matrix SKU row. Each row is one product_sku record.">
            <span>Product</span>
          </Tooltip>
        ),
        key: 'product',
        width: 160,
        fixed: 'left',
        showSorterTooltip: false,
        filters: productFilterOptions,
        filteredValue: tableFilters.product || null,
        onFilter: (value, record) => {
          if (record.groupRow && record.children) {
            return record.children.some((c) => (c.productName || '') === value);
          }
          return (record.productName || '') === value;
        },
        sorter: (a, b) => {
          if (a.groupRow && a.skuTypeGroupKey != null && b.groupRow && b.skuTypeGroupKey != null) {
            return (
              SKU_TYPE_GROUP_ORDER.indexOf(a.skuTypeGroupKey)
              - SKU_TYPE_GROUP_ORDER.indexOf(b.skuTypeGroupKey)
            );
          }
          const cmp = productNameSortKey(a).localeCompare(productNameSortKey(b));
          if (cmp !== 0) return cmp;
          return skuNameSortKey(a).localeCompare(skuNameSortKey(b));
        },
        render: (_, record) => {
          if (record.groupRow) {
            const title = record.groupLabel ?? record.productName ?? '-';
            return (
              <div>
                <Text strong style={{ fontSize: 13 }}>{title}</Text>
                <br />
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {record.children.length}
                  {' '}
                  SKU
                  {record.children.length !== 1 ? 's' : ''}
                </Text>
              </div>
            );
          }
          return (
            <div>
              <Text strong style={{ fontSize: 13 }}>{record.productName || '-'}</Text>
              <br />
              <Text type="secondary" style={{ fontSize: 11 }}>
                {record.productSku?.name || `SKU #${record.productSkuKey}`}
              </Text>
            </div>
          );
        },
      },
      {
        title: (
          <Tooltip title="Link and edit the Cin7/DEAR product line for this SKU. Used when pushing proposal trade prices to Cin7.">
            <span>Cin7</span>
          </Tooltip>
        ),
        key: 'cin7',
        width: 260,
        showSorterTooltip: false,
        sorter: (a, b) => getCin7SkuString(a).localeCompare(getCin7SkuString(b)),
        render: (_, record) => {
          if (record.groupRow) return <Text type="secondary">—</Text>;
          const sku = record.productSku;
          const productId = sku?.productMatrixId ?? sku?.product_matrix_id;
          const skuKey = record.productSkuKey ?? sku?.key;
          if (!productId || !skuKey) return <Text type="secondary">-</Text>;
          return (
            <SkuCin7InlineEditor
              productId={productId}
              skuKey={skuKey}
              sku={sku}
              compact
              parentProductName={record.productName || ''}
              sharedCachedProducts={sharedCin7CachedProducts}
              onSaved={refreshProposalAfterSkuLink}
            />
          );
        },
      },
      {
        title: (
          <Tooltip title="Link Kounta products to this Matrix SKU. Pour sizes and sell prices follow Matrix fields when exporting Kounta CSV from this proposal.">
            <span>Kounta</span>
          </Tooltip>
        ),
        key: 'kounta',
        width: 260,
        showSorterTooltip: false,
        sorter: (a, b) => skuNameSortKey(a).localeCompare(skuNameSortKey(b)),
        render: (_, record) => {
          if (record.groupRow) return <Text type="secondary">—</Text>;
          const sku = record.productSku;
          const skuKey = record.productSkuKey ?? sku?.key;
          if (!skuKey) return <Text type="secondary">-</Text>;
          return (
            <div style={{ maxWidth: 252 }}>
              <KountaProductLinker
                skuKey={skuKey}
                compact
                sharedKountaCatalog={sharedKountaCatalog}
                onLinksChanged={refreshProposalAfterSkuLink}
              />
            </div>
          );
        },
      },
      ...priceFieldsVisible.map((pf) => ({
        title: (
          <Tooltip title={pf.tooltip}>
            <span style={{ fontSize: 11 }}>{pf.label}</span>
          </Tooltip>
        ),
        key: pf.key,
        width: 138,
        showSorterTooltip: false,
        filters: [
          { text: 'Has proposed change', value: 'changed' },
          { text: 'No change', value: 'unchanged' },
        ],
        filteredValue: tableFilters[pf.key] || null,
        filterMultiple: false,
        onFilter: (value, record) => {
          if (record.groupRow && record.children) {
            return record.children.some((c) => priceFilterMatch(c, pf.key, value, editedPrices));
          }
          return priceFilterMatch(record, pf.key, value, editedPrices);
        },
        sorter: (a, b) => {
          const na = priceSortValue(a, pf.key, editedPrices);
          const nb = priceSortValue(b, pf.key, editedPrices);
          if (na == null && nb == null) return 0;
          if (na == null) return 1;
          if (nb == null) return -1;
          return na - nb;
        },
        render: (_, record) => {
          if (record.groupRow) return <Text type="secondary">—</Text>;
          const sku = record.productSku;
          const greyOut = priceFieldGreyOut(sku, pf.key);
          const current = record.currentPrices?.[pf.key];
          const proposed = editedPrices[record.id]?.[pf.key];
          const hasChange = proposed != null && proposed !== '' && proposed !== current;

          const productRange = record.productRange ?? record.product_range;
          const productAbv = record.productAbv ?? record.product_abv;
          const productType = record.productType ?? record.product_type;
          const highPour = isHighAbvOrWild(productAbv, productType, pricingRules);
          const pourCond = highPour
            && (pf.key === 'priceVenuePerLitre'
              || pf.key === 'pricePint'
              || pf.key === 'priceHalfPint'
              || pf.key === 'price150ml')
            ? 'high_abv_or_wild'
            : '';
          const showMargin = !greyOut && !AU_PRICING_KEYS.has(pf.key)
          && SELLING_PRICE_FIELDS_WITH_MARGIN.has(pf.key);
          const currentPricesOnly = record.currentPrices || {};
          const effectivePrices = getEffectivePrices(record, editedPrices[record.id]);
          const curM = showMargin
            ? computeMargin(pf.key, sku, currentPricesOnly)
            : { displayPct: null, isSimple: false };
          const newM = showMargin
            ? computeMargin(pf.key, sku, effectivePrices)
            : { displayPct: null, isSimple: false };
          const targetPct = showMargin
            ? resolveTargetPct(marginTargetMap, productRange, pf.key, pourCond)
            : null;
          const meetsTarget = marginMeetsTarget(newM.displayPct, targetPct);

          const effOn = editedPrices[record.id]?.priceOn ?? record.currentPrices?.priceOn;
          const kegSug = !greyOut && isKegSku(sku) && effOn != null && effOn !== ''
            ? getKegSuggestionsForSku(
              { ...sku, productRange, productType },
              effOn,
              productAbv,
              marginTargetMap,
              { productType, rules: pricingRules },
            )
            : null;
          let matchSug = kegSug?.[pf.key];
          if (matchSug == null && !isKegSku(sku) && !greyOut) {
            const skuCtx = { ...sku, productRange, productType };
            if (pf.key === 'rrpUnit' || pf.key === 'rrpPack' || pf.key === 'rrpCase') {
              const rrp = calculateRrpSuggestions(
                skuCtx, effectivePrices, marginTargetMap, pricingRules,
              );
              matchSug = rrp[pf.key];
            } else if (pf.key === 'priceBar') {
              matchSug = calculateOpSuggestion(skuCtx, effectivePrices, pricingRules);
            }
          }
          const isAutoSuggested = Boolean(
            isDraft
            && !greyOut
            && matchSug != null
            && autoSuggestedFields[record.id]?.[pf.key]
            && valueMatchesSuggestion(
              proposed != null && proposed !== '' ? String(proposed) : '',
              matchSug,
            ),
          );

          let marginPercentColor;
          if (greyOut) {
            marginPercentColor = undefined;
          } else if (isAutoSuggested) {
            marginPercentColor = '#52c41a';
          } else if (hasChange) {
            marginPercentColor = '#1890ff';
          } else {
            marginPercentColor = undefined;
          }

          let inputBgColor;
          let inputBorderColor;
          if (greyOut) {
            inputBgColor = 'transparent';
            inputBorderColor = undefined;
          } else if (isAutoSuggested) {
            inputBgColor = '#f6ffed';
            inputBorderColor = '#52c41a';
          } else if (hasChange) {
            inputBgColor = '#e6f7ff';
            inputBorderColor = '#1890ff';
          } else {
            inputBgColor = undefined;
            inputBorderColor = undefined;
          }

          const inner = (
            <div
              style={{
                padding: greyOut ? '2px 4px' : undefined,
                borderRadius: greyOut ? 4 : undefined,
                background: greyOut ? 'rgba(0,0,0,0.06)' : undefined,
                minHeight: greyOut ? 48 : undefined,
              }}
            >
              <div style={{ fontSize: 11, lineHeight: 1.3 }}>
                <Text
                  type="secondary"
                  style={{
                    textDecoration: hasChange ? 'line-through' : 'none',
                    opacity: greyOut ? 0.55 : undefined,
                  }}
                >
                  {current || '-'}
                </Text>
                {showMargin && curM.displayPct != null && (
                <Text type="secondary" style={{ marginLeft: 4 }}>
                  (
                  {curM.displayPct}
                  %)
                </Text>
                )}
              </div>
              {isDraft && (
              <div
                style={{
                  display: 'flex', alignItems: 'center', gap: 4, marginTop: 2,
                }}
              >
                <InputNumber
                  size="small"
                  disabled={greyOut}
                  style={{
                    flex: 1,
                    minWidth: 52,
                    borderColor: inputBorderColor,
                    backgroundColor: inputBgColor,
                  }}
                  value={proposed != null ? Number(proposed) : undefined}
                  placeholder={current || ''}
                  onChange={(val) => handlePriceChange(record.id, pf.key, val)}
                  step={0.5}
                  min={0}
                  controls={false}
                />
                {showMargin && newM.displayPct != null && (
                  <Text
                    style={{
                      fontSize: 11,
                      whiteSpace: 'nowrap',
                      color: marginPercentColor,
                    }}
                  >
                    {newM.displayPct}
                    %
                  </Text>
                )}
              </div>
              )}
              {!isDraft && hasChange && (
              <div style={{ marginTop: 2 }}>
                <Text strong style={{ color: '#1890ff', fontSize: 12 }}>{proposed}</Text>
                {showMargin && newM.displayPct != null && (
                  <Text style={{ fontSize: 11, marginLeft: 4, color: '#1890ff' }}>
                    (
                    {newM.displayPct}
                    %)
                  </Text>
                )}
              </div>
              )}
              {showMargin && targetPct != null && (
              <div style={{ fontSize: 10, marginTop: 2, lineHeight: 1.2 }}>
                <Text type="secondary">Target: </Text>
                <Text style={{ color: meetsTarget ? '#52c41a' : '#faad14' }}>
                  {targetPct}
                  %
                </Text>
              </div>
              )}
            </div>
          );

          if (greyOut) {
            const tip = priceFieldGreyOutTooltip(sku, pf.key);
            return tip ? <Tooltip title={tip}>{inner}</Tooltip> : inner;
          }
          return inner;
        },
      })),
    ];
    if (isDraft) {
      cols.push({
        title: '',
        key: 'actions',
        width: 50,
        fixed: 'right',
        render: (_, record) => {
          if (record.groupRow) return null;
          return (
            <Popconfirm
              title="Remove from proposal?"
              onConfirm={() => handleRemoveItem(record.id)}
            >
              <Button size="small" icon={<DeleteOutlined />} type="text" danger />
            </Popconfirm>
          );
        },
      });
    }
    return cols;
  }, [
    priceFieldsVisible,
    editedPrices,
    autoSuggestedFields,
    marginTargetMap,
    isDraft,
    tableFilters,
    tableGroupMode,
    productFilterOptions,
    handlePriceChange,
    refreshProposalAfterSkuLink,
    handleRemoveItem,
    sharedKountaCatalog,
    sharedCin7CachedProducts,
  ]);

  const visibleColumns = useMemo(
    () => pricingColumns.filter((col) => {
      const k = col.key;
      if (!k || k === 'product') return true;
      return !effectiveHiddenColumns.has(k);
    }),
    [pricingColumns, effectiveHiddenColumns],
  );

  const toggleColumnHidden = useCallback((key) => {
    setHiddenColumns((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const toggleAuColumnsHidden = useCallback(() => {
    setHiddenColumns((prev) => {
      const next = new Set(prev);
      const allHidden = [...AU_PRICING_KEYS].every((k) => next.has(k));
      if (allHidden) {
        [...AU_PRICING_KEYS].forEach((k) => next.delete(k));
      } else {
        [...AU_PRICING_KEYS].forEach((k) => next.add(k));
      }
      return next;
    });
  }, []);

  const clearTableControls = useCallback(() => {
    setSearchTerm('');
    setTableFilters({});
    setSkuFocusMode(null);
  }, []);

  if (loading) return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;
  if (!proposal) return <Text>Proposal not found</Text>;

  const items = proposalItems;
  const existingSkuKeys = new Set(items.map((i) => i.productSkuKey));
  const applyFieldRows = applyPreviewChanges.filter((c) => c.field && !c.error);
  const applyErrorRows = applyPreviewChanges.filter((c) => c.error);
  const pushRowsOk = pushPreviewItems.filter(
    (i) => !i.error && i.tiers && Object.keys(i.tiers).length > 0,
  );
  const pushErrorRows = pushPreviewItems.filter((i) => i.error);
  const recoverDbFieldRows = recoverDbChanges.filter((c) => c.field && !c.error);
  const recoverDbErrorRows = recoverDbChanges.filter((c) => c.error);
  const recoverCin7Ok = recoverCin7Items.filter(
    (i) => !i.error && i.tiers && Object.keys(i.tiers).length > 0,
  );
  const recoverCin7Errors = recoverCin7Items.filter((i) => i.error);

  const recoverDbFieldRowsFiltered = recoverDbFieldRows.filter((r) => (
    recoverDbSelectedSkuKeys.includes(String(r.skuKey))
  ));
  const recoverDbSkuOptions = (() => {
    const map = new Map();
    recoverDbFieldRows.forEach((r) => {
      const k = String(r.skuKey);
      if (!map.has(k)) map.set(k, r.skuName || k);
    });
    return [...map.entries()].map(([value, label]) => ({ value, label }));
  })();
  const recoverDbSelectAllChecked = recoverDbSkuOptions.length > 0
    && recoverDbSelectedSkuKeys.length === recoverDbSkuOptions.length;
  const recoverDbSelectAllIndeterminate = recoverDbSelectedSkuKeys.length > 0
    && recoverDbSelectedSkuKeys.length < recoverDbSkuOptions.length;

  const fullSendApplyFieldRows = fullSendApplyPreview.filter((c) => c.field && !c.error);
  const fullSendApplyErrorRows = fullSendApplyPreview.filter((c) => c.error);
  const fullSendCin7RowsOk = fullSendCin7Preview.filter(
    (i) => !i.error && i.tiers && Object.keys(i.tiers).length > 0,
  );
  const fullSendCin7ErrorRows = fullSendCin7Preview.filter((i) => i.error);
  const fullSendApplySkuCount = new Set(fullSendApplyFieldRows.map((r) => String(r.skuKey))).size;
  const fullSendHasWork = fullSendApplyFieldRows.length > 0
    || fullSendCin7RowsOk.length > 0;

  return (
    <div>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={onBack} />
          <Title level={4} style={{ margin: 0 }}>{proposal.name}</Title>
          <Tag color={proposal.status === 'applied' ? 'green' : 'blue'}>
            {proposal.status}
          </Tag>
        </Space>
      </div>

      {/* Action bar */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          width: '100%',
          gap: 12,
          marginBottom: 16,
        }}
      >
        {isDraft && (
          <Space wrap align="center" style={{ flex: '0 0 auto' }}>
            <Button
              icon={<PlusOutlined />}
              onClick={() => setSkuPickerVisible(true)}
            >
              Add SKUs
            </Button>
            <Button
              icon={<SaveOutlined />}
              onClick={handleSave}
              loading={saving}
            >
              Save
            </Button>
            <Tooltip title="Fill Venue/L, Pint, Half Pint, and 150ml from each keg’s On Prem price (Monday formulas). Skips rows with no keg price.">
              <Button
                icon={<CalculatorOutlined />}
                onClick={handleBulkAutofillPourPrices}
              >
                Auto-fill Pour Prices
              </Button>
            </Tooltip>
            <Tooltip title="Fill Progressive from each non-keg SKU’s Off Prem (Off Prem + 2.65%; six-pack slabs 4x6 use Off Prem with no markup). Skips kegs and rows with no Off Prem.">
              <Button
                icon={<PercentageOutlined />}
                onClick={handleBulkAutofillProgressivePrices}
              >
                Auto-fill Progressive
              </Button>
            </Tooltip>
            {currentUser?.admin && (
              <Tooltip title="Same setting as Product Database. When on, draft prices save automatically 1.5s after you stop typing.">
                <Space size="small" align="center">
                  <Switch
                    checked={autoSaveEnabled}
                    onChange={onToggleAutoSave}
                    size="small"
                  />
                  <Text type="secondary" style={{ fontSize: 12 }}>Autosave</Text>
                </Space>
              </Tooltip>
            )}
            {currentUser?.admin && lastSaved && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                Last saved
                {' '}
                {lastSaved.format('h:mm:ss a')}
              </Text>
            )}
          </Space>
        )}

        <Space wrap align="center" style={{ flex: '0 0 auto' }}>
          {isDraft && (
            <Button
              type="primary"
              className="pricing-full-send-btn"
              icon={<ThunderboltOutlined />}
              onClick={openFullSendModal}
              loading={fullSendLoading}
              disabled={fullSendLoading}
            >
              Full Send
            </Button>
          )}
          {isDraft && (
            <Button
              type="primary"
              icon={<CheckCircleOutlined />}
              onClick={openApplyPreviewModal}
              loading={applying}
            >
              Apply to DB
            </Button>
          )}
          <Button
            type="primary"
            icon={<CloudUploadOutlined />}
            onClick={openPushPreviewModal}
            loading={pushingCin7}
            disabled={pushingCin7}
          >
            Push to Cin7
          </Button>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={handleExportKounta}
          >
            Export Kounta CSV
          </Button>
          {proposal.status === 'applied' && (
            <>
              <Text type="secondary" style={{ fontSize: 12 }}>Emergency recovery</Text>
              <Button
                size="small"
                icon={<DatabaseOutlined />}
                onClick={openRecoverDbModal}
                loading={recoveringDb}
              >
                Restore Matrix DB
              </Button>
              <Button
                size="small"
                icon={<RollbackOutlined />}
                onClick={openRecoverCin7Modal}
              >
                Restore Cin7
              </Button>
              <Button
                size="small"
                icon={<DownloadOutlined />}
                onClick={handleExportKountaPrevious}
              >
                Previous Kounta CSV
              </Button>
            </>
          )}
        </Space>

        <Space wrap align="center" style={{ marginLeft: 'auto' }}>
          <Button
            icon={<PercentageOutlined />}
            onClick={() => history.push('/dashboard/pricing/guide')}
          >
            Pricing Guide
          </Button>
        </Space>
      </div>

      {/* Pricing table: search, grouping, column visibility */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 12,
          marginBottom: 12,
        }}
      >
        <Input.Search
          placeholder="Search products or SKUs..."
          allowClear
          style={{ width: 280 }}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <Space size="small" align="center">
          <ColumnHeightOutlined style={{ color: 'rgba(0,0,0,0.45)' }} />
          <Select
            size="small"
            style={{ minWidth: 168 }}
            value={tableGroupMode}
            onChange={setTableGroupMode}
            options={[
              { value: 'flat', label: 'No grouping' },
              { value: 'product', label: 'By product' },
              {
                value: 'skuType',
                label: 'By SKU type (keg / can / bottle)',
              },
              { value: 'volume', label: 'By volume (unit size)' },
            ]}
          />
        </Space>
        <Space size="small" align="center" wrap>
          <Text type="secondary" style={{ fontSize: 12 }}>SKU focus</Text>
          <Radio.Group
            size="small"
            value={skuFocusMode ?? 'all'}
            onChange={(e) => {
              const v = e.target.value;
              setSkuFocusMode(v === 'all' ? null : v);
            }}
          >
            <Radio.Button value="all">All</Radio.Button>
            <Radio.Button value="keg">{SKU_TYPE_GROUP_LABELS.keg}</Radio.Button>
            <Radio.Button value="can">{SKU_TYPE_GROUP_LABELS.can}</Radio.Button>
            <Radio.Button value="bottle">{SKU_TYPE_GROUP_LABELS.bottle}</Radio.Button>
          </Radio.Group>
        </Space>
        <Dropdown
          trigger={['click']}
          overlay={(
            <div
              style={{
                padding: 12,
                minWidth: 280,
                maxHeight: 400,
                overflowY: 'auto',
                background: '#fff',
                boxShadow: '0 3px 6px -4px rgba(0,0,0,0.12), 0 6px 16px 0 rgba(0,0,0,0.08)',
              }}
            >
              <Space direction="vertical" style={{ width: '100%' }} size={4}>
                <Text strong style={{ fontSize: 12 }}>Show columns</Text>
                <Checkbox checked disabled>Product</Checkbox>
                <Checkbox
                  checked={!effectiveHiddenColumns.has('cin7')}
                  onChange={() => toggleColumnHidden('cin7')}
                >
                  Cin7
                </Checkbox>
                <Checkbox
                  checked={!effectiveHiddenColumns.has('kounta')}
                  onChange={() => toggleColumnHidden('kounta')}
                >
                  Kounta
                </Checkbox>
                {PRICE_FIELDS.map((pf) => {
                  const forcedByFocus = Boolean(skuFocusMode && focusHiddenPriceKeys.has(pf.key));
                  const cb = (
                    <Checkbox
                      checked={!effectiveHiddenColumns.has(pf.key)}
                      disabled={forcedByFocus}
                      onChange={() => toggleColumnHidden(pf.key)}
                    >
                      {pf.label}
                    </Checkbox>
                  );
                  return (
                    <div key={pf.key}>
                      {forcedByFocus ? (
                        <Tooltip
                          title={`Hidden in ${SKU_TYPE_GROUP_LABELS[skuFocusMode]} focus`}
                        >
                          <span>{cb}</span>
                        </Tooltip>
                      ) : cb}
                    </div>
                  );
                })}
                {isDraft && (
                  <Checkbox
                    checked={!effectiveHiddenColumns.has('actions')}
                    onChange={() => toggleColumnHidden('actions')}
                  >
                    Actions
                  </Checkbox>
                )}
                <Divider style={{ margin: '8px 0' }} />
                <Button size="small" type="default" block onClick={toggleAuColumnsHidden}>
                  Toggle AU columns (AU, VIC/SA, QLD/WA)
                </Button>
              </Space>
            </div>
          )}
        >
          <Button icon={<SettingOutlined />}>Columns</Button>
        </Dropdown>
        <Button
          onClick={clearTableControls}
          disabled={
            !searchTerm
            && !Object.keys(tableFilters || {}).length
            && !skuFocusMode
          }
        >
          Clear search &amp; filters
        </Button>
      </div>

      {/* Cin7 push progress */}
      {cin7Status && cin7Status.status === 'running' && (
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary">Pushing to Cin7...</Text>
          <Progress
            percent={
              cin7Status.total
                ? Math.round((cin7Status.processed / cin7Status.total) * 100)
                : 0
            }
            size="small"
            status="active"
          />
        </div>
      )}

      {/* Pricing grid */}
      <Table
        dataSource={tableDataSource}
        columns={visibleColumns}
        rowKey={(r) => (r.groupRow ? r.groupKey : r.id)}
        pagination={false}
        scroll={{ x: 'max-content' }}
        size="small"
        bordered
        onChange={(pagination, filters) => {
          setTableFilters(filters || {});
        }}
        expandable={tableGroupMode !== 'flat' ? { defaultExpandAllRows: true } : undefined}
        rowClassName={(record) => (record.groupRow ? 'pricing-manager-group-row' : '')}
      />

      {/* SKU picker modal */}
      <Modal
        title="Add SKUs to Proposal"
        visible={skuPickerVisible}
        onOk={handleAddSkus}
        onCancel={() => { setSkuPickerVisible(false); setSelectedSkuKeys([]); setSkuSearchTerm(''); }}
        okText={`Add ${selectedSkuKeys.length} SKU${selectedSkuKeys.length !== 1 ? 's' : ''}`}
        okButtonProps={{ disabled: !selectedSkuKeys.length }}
        width={960}
        destroyOnClose
      >
        <SkuPicker
          existingSkuKeys={existingSkuKeys}
          selectedKeys={selectedSkuKeys}
          onSelectionChange={setSelectedSkuKeys}
          searchTerm={skuSearchTerm}
          onSearchChange={setSkuSearchTerm}
          cin7CachedProducts={
            Array.isArray(sharedCin7CachedProducts)
              ? sharedCin7CachedProducts
              : undefined
          }
        />
      </Modal>

      <Modal
        title="Confirm apply to Matrix database"
        visible={applyConfirmOpen}
        onCancel={() => setApplyConfirmOpen(false)}
        onOk={async () => {
          setApplyConfirmOpen(false);
          await handleApply();
        }}
        okText="Apply to database"
        okButtonProps={{
          disabled: applyPreviewLoading || applyFieldRows.length === 0,
          loading: applying,
        }}
        width={760}
        destroyOnClose
      >
        {applyPreviewLoading ? (
          <Spin style={{ display: 'block', margin: '24px auto' }} />
        ) : (
          <>
            <Paragraph type="secondary" style={{ marginBottom: 12 }}>
              The following price fields will be written to the database and relevant changes synced to Monday.com.
            </Paragraph>
            {applyErrorRows.length > 0 && (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 12 }}
                message={(
                  <span>
                    {applyErrorRows.length}
                    {' '}
                    item(s) could not be previewed (e.g. missing SKU).
                  </span>
                )}
              />
            )}
            {!applyPreviewLoading && applyFieldRows.length === 0 && (
              <Alert type="info" showIcon message="No price changes to apply." />
            )}
            <Table
              size="small"
              pagination={false}
              scroll={{ y: 360 }}
              rowKey={(r, i) => `${r.skuKey}-${r.field}-${i}`}
              dataSource={applyFieldRows}
              columns={[
                {
                  title: 'SKU',
                  dataIndex: 'skuName',
                  key: 'sku',
                  width: 160,
                  ellipsis: true,
                },
                {
                  title: 'Field',
                  dataIndex: 'field',
                  key: 'field',
                  width: 120,
                  render: (f) => SNAKE_FIELD_LABEL[f] || f,
                },
                {
                  title: 'Current',
                  dataIndex: 'oldValue',
                  key: 'old',
                  render: (v) => (v == null || v === '' ? '—' : String(v)),
                },
                {
                  title: 'After apply',
                  dataIndex: 'newValue',
                  key: 'new',
                  render: (v) => (v == null || v === '' ? '—' : String(v)),
                },
              ]}
            />
          </>
        )}
      </Modal>

      <Modal
        title="Confirm push to Cin7"
        visible={pushConfirmOpen}
        onCancel={() => setPushConfirmOpen(false)}
        onOk={async () => {
          setPushConfirmOpen(false);
          await handlePushCin7();
        }}
        okText="Push to Cin7"
        okButtonProps={{
          disabled: pushPreviewLoading || pushRowsOk.length === 0,
          loading: pushingCin7,
        }}
        width={800}
        destroyOnClose
      >
        {pushPreviewLoading ? (
          <Spin style={{ display: 'block', margin: '24px auto' }} />
        ) : (
          <>
            <Paragraph type="secondary" style={{ marginBottom: 12 }}>
              NZ trade prices below will be mapped to Cin7/DEAR product price tiers (On Prem, Off Prem, Progressive, Corporate).
            </Paragraph>
            {pushErrorRows.length > 0 && (
              <Alert type="warning" showIcon style={{ marginBottom: 12 }} message={`${pushErrorRows.length} SKU(s) skipped in preview.`} />
            )}
            {!pushPreviewLoading && pushRowsOk.length === 0 && (
              <Alert type="info" showIcon message="Nothing to push — no Cin7 tier payload could be built from this proposal." />
            )}
            <Table
              size="small"
              pagination={false}
              scroll={{ y: 360 }}
              rowKey={(r) => r.skuKey}
              dataSource={pushPreviewItems.filter((i) => !i.error)}
              columns={[
                {
                  title: 'SKU',
                  dataIndex: 'skuName',
                  key: 'sku',
                  width: 140,
                  ellipsis: true,
                },
                {
                  title: 'Cin7 product ID',
                  dataIndex: 'cin7ProductId',
                  key: 'id',
                  width: 200,
                  ellipsis: true,
                },
                {
                  title: 'Source (NZ)',
                  key: 'src',
                  render: (_, r) => (
                    <span style={{ fontSize: 12 }}>
                      On:
                      {' '}
                      {r.sourcePrices?.price_on ?? '—'}
                      {' · '}
                      Off:
                      {' '}
                      {r.sourcePrices?.price_off ?? '—'}
                      {' · '}
                      Prog:
                      {' '}
                      {r.sourcePrices?.price_progressive ?? '—'}
                    </span>
                  ),
                },
                {
                  title: 'Tiers sent to Cin7',
                  dataIndex: 'tiers',
                  key: 'tiers',
                  render: (tiers) => (
                    <span style={{ fontSize: 11, wordBreak: 'break-all' }}>{formatCin7Tiers(tiers)}</span>
                  ),
                },
              ]}
            />
          </>
        )}
      </Modal>

      <Modal
        title="Full Send — all platforms"
        visible={fullSendOpen}
        onCancel={() => setFullSendOpen(false)}
        onOk={handleFullSend}
        okText="Full Send"
        okButtonProps={{
          disabled: fullSendLoading || !fullSendHasWork,
          loading: fullSending || applying,
          danger: true,
        }}
        width={840}
        destroyOnClose
      >
        {fullSendLoading ? (
          <Spin style={{ display: 'block', margin: '24px auto' }} />
        ) : (
          <>
            <Paragraph style={{ marginBottom: 12 }}>
              <Text strong>Summary</Text>
              <br />
              <Text type="secondary">
                This will, in order: apply prices to the Matrix database (Monday.com sync), push NZ trade tiers to Cin7, then download the Kounta CSV for manual import.
              </Text>
            </Paragraph>
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message={(
                <div>
                  <div>
                    <Text strong>Matrix DB / Monday.com:</Text>
                    {' '}
                    {fullSendApplyFieldRows.length}
                    {' '}
                    field change(s) across
                    {' '}
                    {fullSendApplySkuCount}
                    {' '}
                    SKU(s)
                  </div>
                  <div style={{ marginTop: 8 }}>
                    <Text strong>Cin7:</Text>
                    {' '}
                    {fullSendCin7RowsOk.length}
                    {' '}
                    SKU(s) with tier updates
                  </div>
                  <div style={{ marginTop: 8 }}>
                    <Text strong>Kounta:</Text>
                    {' '}
                    CSV download (import manually in Kounta)
                  </div>
                </div>
              )}
            />
            {fullSendApplyErrorRows.length > 0 && (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 12 }}
                message={`${fullSendApplyErrorRows.length} item(s) could not be previewed for Matrix apply.`}
              />
            )}
            {fullSendCin7ErrorRows.length > 0 && (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 12 }}
                message={`${fullSendCin7ErrorRows.length} SKU(s) skipped in Cin7 preview.`}
              />
            )}
            {!fullSendHasWork && (
              <Alert type="info" showIcon message="Nothing to send — no apply changes and no Cin7 tiers from this proposal." />
            )}
            <Collapse defaultActiveKey={fullSendHasWork ? ['apply', 'cin7'] : []} ghost>
              <Collapse.Panel header="Matrix / Monday.com — field changes" key="apply">
                <Table
                  size="small"
                  pagination={false}
                  scroll={{ y: 280 }}
                  rowKey={(r, i) => `${r.skuKey}-${r.field}-${i}`}
                  dataSource={fullSendApplyFieldRows}
                  columns={[
                    {
                      title: 'SKU',
                      dataIndex: 'skuName',
                      key: 'sku',
                      width: 160,
                      ellipsis: true,
                    },
                    {
                      title: 'Field',
                      dataIndex: 'field',
                      key: 'field',
                      width: 120,
                      render: (f) => SNAKE_FIELD_LABEL[f] || f,
                    },
                    {
                      title: 'Current',
                      dataIndex: 'oldValue',
                      key: 'old',
                      render: (v) => (v == null || v === '' ? '—' : String(v)),
                    },
                    {
                      title: 'After apply',
                      dataIndex: 'newValue',
                      key: 'new',
                      render: (v) => (v == null || v === '' ? '—' : String(v)),
                    },
                  ]}
                />
              </Collapse.Panel>
              <Collapse.Panel header="Cin7 — tier preview" key="cin7">
                <Table
                  size="small"
                  pagination={false}
                  scroll={{ y: 280 }}
                  rowKey={(r) => r.skuKey}
                  dataSource={fullSendCin7Preview.filter((i) => !i.error)}
                  columns={[
                    {
                      title: 'SKU',
                      dataIndex: 'skuName',
                      key: 'sku',
                      width: 140,
                      ellipsis: true,
                    },
                    {
                      title: 'Cin7 product ID',
                      dataIndex: 'cin7ProductId',
                      key: 'id',
                      width: 200,
                      ellipsis: true,
                    },
                    {
                      title: 'Source (NZ)',
                      key: 'src',
                      render: (_, r) => (
                        <span style={{ fontSize: 12 }}>
                          On:
                          {' '}
                          {r.sourcePrices?.price_on ?? '—'}
                          {' · '}
                          Off:
                          {' '}
                          {r.sourcePrices?.price_off ?? '—'}
                          {' · '}
                          Prog:
                          {' '}
                          {r.sourcePrices?.price_progressive ?? '—'}
                        </span>
                      ),
                    },
                    {
                      title: 'Tiers sent to Cin7',
                      dataIndex: 'tiers',
                      key: 'tiers',
                      render: (tiers) => (
                        <span style={{ fontSize: 11, wordBreak: 'break-all' }}>{formatCin7Tiers(tiers)}</span>
                      ),
                    },
                  ]}
                />
              </Collapse.Panel>
            </Collapse>
          </>
        )}
      </Modal>

      <Modal
        title="Restore Matrix database from snapshot?"
        visible={recoverDbOpen}
        onCancel={() => setRecoverDbOpen(false)}
        onOk={confirmRecoverDb}
        okText="Restore database"
        okButtonProps={{
          disabled: recoverDbLoading
            || recoverDbFieldRowsFiltered.length === 0
            || recoverDbSelectedSkuKeys.length === 0,
          loading: recoveringDb,
          danger: true,
        }}
        width={760}
        destroyOnClose
      >
        {recoverDbLoading ? (
          <Spin style={{ display: 'block', margin: '24px auto' }} />
        ) : (
          <>
            <Paragraph type="secondary" style={{ marginBottom: 12 }}>
              Overwrites current database prices with the snapshot stored when each SKU was added to this proposal (not necessarily the moment before Apply). Monday.com will be synced for changed fields.
            </Paragraph>
            {recoverDbErrorRows.length > 0 && (
              <Alert type="warning" showIcon style={{ marginBottom: 12 }} message={`${recoverDbErrorRows.length} item(s) could not be previewed.`} />
            )}
            {!recoverDbLoading && recoverDbFieldRows.length === 0 && (
              <Alert type="info" showIcon message="No differences — database already matches the stored snapshot." />
            )}
            {recoverDbSkuOptions.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <Checkbox
                  checked={recoverDbSelectAllChecked}
                  indeterminate={recoverDbSelectAllIndeterminate}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setRecoverDbSelectedSkuKeys(recoverDbSkuOptions.map((o) => o.value));
                    } else {
                      setRecoverDbSelectedSkuKeys([]);
                    }
                  }}
                >
                  Select all
                  {' '}
                  (
                  {recoverDbSkuOptions.length}
                  {' '}
                  SKU
                  {recoverDbSkuOptions.length !== 1 ? 's' : ''}
                  )
                </Checkbox>
                <Checkbox.Group
                  value={recoverDbSelectedSkuKeys}
                  onChange={(vals) => setRecoverDbSelectedSkuKeys(vals)}
                  style={{ display: 'block', marginTop: 8 }}
                >
                  <Space direction="vertical" size={4} style={{ width: '100%', maxHeight: 160, overflowY: 'auto' }}>
                    {recoverDbSkuOptions.map((o) => (
                      <Checkbox key={o.value} value={o.value}>{o.label}</Checkbox>
                    ))}
                  </Space>
                </Checkbox.Group>
              </div>
            )}
            <Table
              size="small"
              pagination={false}
              scroll={{ y: 360 }}
              rowKey={(r, i) => `${r.skuKey}-${r.field}-${i}`}
              dataSource={recoverDbFieldRowsFiltered}
              columns={[
                {
                  title: 'SKU',
                  dataIndex: 'skuName',
                  key: 'sku',
                  width: 160,
                  ellipsis: true,
                },
                {
                  title: 'Field',
                  dataIndex: 'field',
                  key: 'field',
                  width: 120,
                  render: (f) => SNAKE_FIELD_LABEL[f] || f,
                },
                {
                  title: 'Current',
                  dataIndex: 'oldValue',
                  key: 'old',
                  render: (v) => (v == null || v === '' ? '—' : String(v)),
                },
                {
                  title: 'Restore to',
                  dataIndex: 'newValue',
                  key: 'new',
                  render: (v) => (v == null || v === '' ? '—' : String(v)),
                },
              ]}
            />
          </>
        )}
      </Modal>

      <Modal
        title="Restore Cin7 from snapshot?"
        visible={recoverCin7Open}
        onCancel={() => setRecoverCin7Open(false)}
        onOk={confirmRecoverCin7}
        okText="Restore Cin7"
        okButtonProps={{
          disabled: recoverCin7Loading
            || recoverCin7Ok.length === 0
            || recoverCin7SelectedKeys.length === 0,
          loading: pushingCin7,
          danger: true,
        }}
        width={800}
        destroyOnClose
      >
        {recoverCin7Loading ? (
          <Spin style={{ display: 'block', margin: '24px auto' }} />
        ) : (
          <>
            <Paragraph type="secondary" style={{ marginBottom: 12 }}>
              Pushes the same NZ trade snapshot used for Matrix recovery to Cin7/DEAR (tiers rebuilt from snapshot prices). Use row checkboxes to include only specific SKUs.
            </Paragraph>
            {recoverCin7Errors.length > 0 && (
              <Alert type="warning" showIcon style={{ marginBottom: 12 }} message={`${recoverCin7Errors.length} SKU(s) skipped.`} />
            )}
            {!recoverCin7Loading && recoverCin7Ok.length === 0 && (
              <Alert type="info" showIcon message="Nothing to send — no tier payload from snapshot prices." />
            )}
            <Table
              size="small"
              pagination={false}
              scroll={{ y: 360 }}
              rowKey={(r) => r.skuKey}
              dataSource={recoverCin7Items.filter((i) => !i.error)}
              rowSelection={{
                selectedRowKeys: recoverCin7SelectedKeys,
                onChange: (keys) => setRecoverCin7SelectedKeys(keys),
                getCheckboxProps: (record) => ({
                  disabled: !!(record.error || !record.tiers || !Object.keys(record.tiers || {}).length),
                }),
              }}
              columns={[
                {
                  title: 'SKU',
                  dataIndex: 'skuName',
                  key: 'sku',
                  width: 140,
                  ellipsis: true,
                },
                {
                  title: 'Cin7 product ID',
                  dataIndex: 'cin7ProductId',
                  key: 'id',
                  width: 200,
                  ellipsis: true,
                },
                {
                  title: 'Source (snapshot)',
                  key: 'src',
                  render: (_, r) => (
                    <span style={{ fontSize: 12 }}>
                      On:
                      {' '}
                      {r.sourcePrices?.price_on ?? '—'}
                      {' · '}
                      Off:
                      {' '}
                      {r.sourcePrices?.price_off ?? '—'}
                      {' · '}
                      Prog:
                      {' '}
                      {r.sourcePrices?.price_progressive ?? '—'}
                    </span>
                  ),
                },
                {
                  title: 'Tiers sent to Cin7',
                  dataIndex: 'tiers',
                  key: 'tiers',
                  render: (tiers) => (
                    <span style={{ fontSize: 11, wordBreak: 'break-all' }}>{formatCin7Tiers(tiers)}</span>
                  ),
                },
              ]}
            />
          </>
        )}
      </Modal>
    </div>
  );
};

ProposalEditor.propTypes = {
  proposalId: PropTypes.number.isRequired,
  onBack: PropTypes.func.isRequired,
};

export default ProposalEditor;
